import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';

import { storableError } from '../../util/errors';
import { denormalisedResponseEntities } from '../../util/data';
import { getProcess, getSupportedProcessesInfo } from '../../transactions/transaction';
import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { fetchCurrentUserThunk } from '../../ducks/user.duck';

const EARNINGS_PAGE_SIZE = 100;
const MAX_EARNINGS_PAGES = 50;

const EMPTY_DASHBOARD = {
  activeListingsCount: 0,
  totalEarned: { amount: 0, currency: 'USD' },
  totalSalesCount: 0,
  recentActivity: [],
};

const moneyAmountAsNumber = payout => {
  const amount = payout?.amount;
  if (amount == null) {
    return 0;
  }
  if (typeof amount.toNumber === 'function') {
    return amount.toNumber();
  }
  return Number(amount);
};

const isSaleCompleted = tx => {
  const processName = tx?.attributes?.processName;
  if (!processName) {
    return false;
  }
  try {
    const process = getProcess(processName);
    const state = process.getState(tx);
    if (state === process.states.COMPLETED) {
      return true;
    }
    const lastTransition = tx?.attributes?.lastTransition;
    return !!(lastTransition && process.isCompleted(lastTransition));
  } catch {
    return false;
  }
};

const fetchTotalEarnedSubunits = async (sdk, processNames, marketplaceCurrency) => {
  let page = 1;
  let totalPages = 1;
  let sum = 0;

  while (page <= totalPages && page <= MAX_EARNINGS_PAGES) {
    const response = await sdk.transactions.query({
      only: 'sale',
      processNames,
      page,
      perPage: EARNINGS_PAGE_SIZE,
      'fields.transaction': ['processName', 'lastTransition', 'payoutTotal'],
    });
    totalPages = response.data.meta.totalPages;
    response.data.data.forEach(tx => {
      if (!isSaleCompleted(tx)) {
        return;
      }
      const payout = tx.attributes?.payoutTotal;
      if (!payout || payout.currency !== marketplaceCurrency) {
        return;
      }
      sum += moneyAmountAsNumber(payout);
    });
    page += 1;
  }

  return { amount: sum, currency: marketplaceCurrency };
};

export const loadSellerDashboardThunk = createAsyncThunk(
  'SellerDashboardPage/loadData',
  async ({ marketplaceCurrency } = {}, { dispatch, getState, extra: sdk, rejectWithValue }) => {
    try {
      await dispatch(fetchCurrentUserThunk({ updateNotifications: true, enforce: true })).unwrap();
    } catch (e) {
      return rejectWithValue(storableError(e));
    }

    const user = getState().user?.currentUser;
    if (!user?.id?.uuid || !sdk) {
      return EMPTY_DASHBOARD;
    }

    const processNames = getSupportedProcessesInfo().map(p => p.name);
    const currency = marketplaceCurrency || 'USD';

    try {
      const [published, salesMeta, recentSales, totalEarned] = await Promise.all([
        sdk.ownListings.query({
          states: ['published'],
          page: 1,
          perPage: 1,
          'fields.listing': ['title'],
        }),
        sdk.transactions.query({
          only: 'sale',
          processNames,
          page: 1,
          perPage: 1,
          'fields.transaction': ['lastTransition'],
        }),
        sdk.transactions.query({
          only: 'sale',
          processNames,
          page: 1,
          perPage: 5,
          sort: '-lastTransitionedAt',
          include: ['listing'],
          'fields.transaction': ['lastTransition', 'lastTransitionedAt', 'processName'],
          'fields.listing': ['title'],
        }),
        fetchTotalEarnedSubunits(sdk, processNames, currency),
      ]);

      dispatch(addMarketplaceEntities(recentSales));

      const denormalised = denormalisedResponseEntities(recentSales);
      const recentActivity = denormalised
        .map(tx => ({
          id: tx.id.uuid,
          title: tx.listing?.attributes?.title || '',
          lastTransitionedAt: tx.attributes?.lastTransitionedAt,
        }))
        .sort((a, b) => {
          const tb = new Date(b.lastTransitionedAt || 0).getTime();
          const ta = new Date(a.lastTransitionedAt || 0).getTime();
          const vb = Number.isNaN(tb) ? 0 : tb;
          const va = Number.isNaN(ta) ? 0 : ta;
          return vb - va;
        });

      return {
        activeListingsCount: published.data.meta.totalItems,
        totalEarned,
        totalSalesCount: salesMeta.data.meta.totalItems,
        recentActivity,
      };
    } catch (e) {
      return rejectWithValue(storableError(e));
    }
  }
);

const sellerDashboardPageSlice = createSlice({
  name: 'SellerDashboardPage',
  initialState: {
    loadInProgress: false,
    loadError: null,
    activeListingsCount: null,
    totalEarned: null,
    totalSalesCount: null,
    recentActivity: [],
  },
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(loadSellerDashboardThunk.pending, state => {
        state.loadInProgress = true;
        state.loadError = null;
        state.activeListingsCount = null;
        state.totalEarned = null;
        state.totalSalesCount = null;
        state.recentActivity = [];
      })
      .addCase(loadSellerDashboardThunk.fulfilled, (state, action) => {
        state.loadInProgress = false;
        state.activeListingsCount = action.payload.activeListingsCount;
        state.totalEarned = action.payload.totalEarned;
        state.totalSalesCount = action.payload.totalSalesCount;
        state.recentActivity = action.payload.recentActivity;
      })
      .addCase(loadSellerDashboardThunk.rejected, (state, action) => {
        state.loadInProgress = false;
        state.loadError = action.payload || action.error;
        state.activeListingsCount = null;
        state.totalEarned = null;
        state.totalSalesCount = null;
        state.recentActivity = [];
      });
  },
});

export default sellerDashboardPageSlice.reducer;

/**
 * SSR / route loadData entry (see routeConfiguration.js and pageDataLoadingAPI.js).
 */
export const loadData = (params, search, config) => (dispatch, getState, sdk) => {
  return dispatch(loadSellerDashboardThunk({ marketplaceCurrency: config?.currency }));
};
